import React from "react";
import { Image } from "react-bootstrap";

import Cover2 from "../../../assets/images/articles/history/cover2.png";
import Citations from "../Citations";
import { parseCitations } from "../utils/parse-citations";

const citations = [
  { id: 1, text: "van Middendorp, J. J., et al. (2010). The Edwin Smith papyrus: a clinical reappraisal of the oldest known medical document. Spinal Cord, 48, 786–790. https://doi.org/10.1038/sc.2010.56" },
  { id: 2, text: "Elsayad, K. (2023). What ancient Egyptian medicine can teach us. ASCO Daily News. https://doi.org/10.1200/EDBK_100018" },
  { id: 3, text: "David, R. (2008). The art of healing in ancient Egypt: a scientific reappraisal. The Lancet, 372(9652), 1802–1803." },
  { id: 4, text: "Adu-Gyamfi, S., et al. (2015). Ancient Egyptian medicine: A systematic review. Philosophy, Social and Human Disciplines, 2015(II), 11–20." },
  { id: 5, text: "Metwaly, A. M., et al. (2021). Traditional ancient Egyptian medicine: A review. Pharmacological Research, 173, 105826. https://doi.org/10.1016/j.phrs.2021.105826" },
  { id: 6, text: "Todd, T. W. (1921). Egyptian medicine: A critical study of recent claims. JAMA, 77(8), 557–563." },
  { id: 7, text: "Laudan, L. (1968). Theories of scientific method from Plato to Mach. History of Science, 7(1), 1–63." },
  { id: 8, text: "Ebsco Research Starters. (2018). Aristotle on science as a discipline. Research Starters: Academic Topic Overviews." },
  { id: 9, text: "Falcon, A. (2019). Definition, explanation, and scientific method in Aristotle: On sleep and waking. Scielo Brasil. https://doi.org/10.1590/0100-6045.2019.V42N4.AF" },
  { id: 10, text: "Byrne, C. (2020). Aristotle and scientific experiments. Dialogue: Canadian Philosophical Review, 59(4), 515–536. https://doi.org/10.1017/S0012217320000244" },
  { id: 11, text: "Steffens, B. (2006). Ibn al-Haytham: First scientist. Morgan Reynolds Publishing." },
  { id: 12, text: "Tbakhi, A., & Amr, S. S. (2007). Ibn Al-Haytham: Father of modern optics. Annals of Saudi Medicine, 27(6), 464–467. https://doi.org/10.5144/0256-4947.2007.464" }
];

export default function HistoryOfScientificMethodPart2() {
  return (
    <article
      className="px-3 px-md-4 py-4"
      style={{
        fontSize: "1.05rem",
        lineHeight: 1.8,
        color: "#212529",
        maxWidth: "900px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <header className="text-center mb-5">
        <h2 className="fw-bold mb-3">
          The History of the Scientific Method: Part 2
        </h2>
        <p className="text-muted">Ancient Science: Egypt, Greece, & the Islamic Golden Age</p>

        <Image
          src={Cover2}
          alt="ancient science"
          fluid
          rounded
          className="my-4 shadow-sm"
        />
      </header>

      {/* Full Article Content */}
      <section>
        <p>
          {parseCitations(`
            The scientific method didn't emerge fully formed in 17th century Europe. Its roots stretch back millennia, across civilizations that developed systematic ways of observing, reasoning about, and recording the natural world. From Egyptian medical records to Greek logic to Islamic experimental physics, each tradition contributed essential pieces to what we now recognize as scientific inquiry.`)}
        </p>
      </section>

      <h2 className="fw-bold mt-5 mb-3">Egypt: When Doctors Started Taking Notes</h2>

      <section>
        <p>
          {parseCitations(`
            Around 1600 BCE, Egyptian physicians were doing something remarkable: they were writing down what they observed, and they were doing it systematically. The Edwin Smith Papyrus, preserved from ancient Egypt's Dynasties 16–17, contains 48 medical case histories organized by anatomical region. Each follows the same four-step approach: examination, diagnosis, treatment, and prognosis. Read today, they feel surprisingly modern, almost like clinical charts recorded 3,600 years ago [1][2][3].`)}
        </p>

        <p>
          {parseCitations(`
            What distinguishes the Edwin Smith Papyrus is its rejection of magical explanations. Instead of invoking divine intervention, these physicians documented observable symptoms, palpated injuries, took pulses, and prescribed rational treatments such as sutures, bandaging, splints, and honey to prevent infection. The papyrus contains the first known descriptions of cranial structures, meninges, the external brain surface, cerebrospinal fluid, and intracranial pulsations [1]. It even records the relationship between brain injury location and which side of the body shows paralysis.`)}
        </p>

        <p>
          {parseCitations(`
            Recent multidisciplinary research confirms these were more than just lucky guesses: approximately 64% of remedies documented in Egyptian medical papyri show therapeutic effectiveness by modern standards [3]. And the prescriptions are detailed enough that researchers today can actually reproduce them[3].`)}
        </p>

        <p>
          {parseCitations(`
            This was a system: structured observation plus detailed documentation. Egyptians understood that knowledge preserved in writing could outlive individual practitioners and be transmitted to future generations. Other papyri from the same era - the Moscow Mathematical Papyrus (c. 1850 BCE) and the Rhind Mathematical Papyrus (c. 1550 BCE) demonstrate comparable systematic problem-solving approaches in mathematics [4]. The Ebers Papyrus (c.1550 BCE) similarly contains evidence of traditional empiricism in pharmacology, documenting 876 prescriptions from 328 ingredients [5][6].`)}
        </p>

        <p>
          {parseCitations(`
            These ancient texts represent the earliest known examples of what would eventually become laboratory notebooks: detailed records that make knowledge reproducible.[5][6].`)}
        </p>
      </section>

      <h2 className="fw-bold mt-5 mb-3">Greece: Building the Logical Framework</h2>

      <section>
        <p>
          {parseCitations(`
            Ancient Greek philosophers took a different approach. Rather than focusing on empirical observation, they developed sophisticated frameworks for reasoning about the natural world.`)}
        </p>

        <p>
          {parseCitations(`
            Thales of Miletus (c. 624–546 BCE) made a crucial philosophical leap: he insisted that every event had a natural cause [7]. This shift from mythological to naturalistic explanations opened the door for scientific inquiry.`)}
        </p>

        <p>
          {parseCitations(`
            Leucippus and Democritus pushed further, proposing atomism, the idea that everything is composed of indivisible particles they called atoms. This is one of antiquity's most sophisticated theoretical frameworks, developed through pure reasoning [7].`)}
        </p>

        <p>
          {parseCitations(`
            But it was Aristotle (384–322 BCE) who came closest to articulating something like the scientific method. His approach combined inductive and deductive reasoning: observe specific instances, infer general principles, then test those principles against new observations in continuous cycles [7][8][9]. Aristotle's Organon, a collection of six works on logic, provided a structure for scientific reasoning that would dominate western thought for nearly two millennia [7].`)}
        </p>

        <p>
          {parseCitations(`
            Aristotle pioneered crucial insights: that there are multiple disciplines of knowledge, each with its own methods and standards of rigor; that investigation must begin with observable data; and that understanding requires connecting causes to effects through logical demonstration. His Posterior Analytics laid out techniques for achieving scientific knowledge (episteme) through systematic inquiry. `)}
        </p>

        <p>
          {parseCitations(`
            Yet Aristotle had a critical blind spot. While he insisted on starting with observation, he ultimately relied on intuition (what he called nous) rather than systematic experimentation to grasp foundational principles [7][9]. This reliance on intuitive insight over empirical testing would limit scientific progress for centuries.`)}
        </p>

        <p>
          {parseCitations(`
            Recent scholarship shows that Aristotle did perform experiments, particularly in biology [10]. But his experimental practice remained constrained by his theoretical framework, which privileged logical deduction over systematic testing.`)}
        </p>
      </section>

      <h2 className="fw-bold mt-5 mb-3">The Islamic Golden Age: Putting It All Together</h2>

      <section>
        <p>
          {parseCitations(`
            While Greek thinkers developed frameworks for reasoning, it was the scholars of the, Islamic Golden Age (8th–14th centuries) who advanced systematic experimentation – the missing component that would eventually complete the scientific method
            Across centers of learning in Baghdad, Cairo, and Córdoba, scholars emphasized careful measurement, empirical testing, and the use of mathematics to clarify physical principles. Physicians like Al-Razi compared treatments through direct observation, while figures such as Ibn Sina (Avicenna) described controlled procedures for testing the efficacy of drugs. Their work created an intellectual culture that prioritized verification over tradition.
          `)}
        </p>

        <p>
          {parseCitations(`
            Within this environment, Abu Ali al-Hasan ibn al-Haytham (965–1040 CE), an Iraqi mathematician and physicist, produced one of the clearest articulations of experimental science. According to historical accounts, he was confined in Cairo after proposing, but failing to execute, a major engineering project[11][12]. During his imprisonment, he wrote his groundbreaking seven-volume treatise Kitab al-Manazir (Book of Optics), which historians consistently rank among the most influential scientific texts in history[11][12].`)}
        </p>

        <p>
          {parseCitations(`
            What distinguished Ibn al-Haytham's work was his approach. He argued that scientific claims must be grounded observation, hypothesis formation, and experimentation, and independent verification [11][12]. His famous camera obscura experiments provided empirical evidence that light travels in straight lines into the eye, overturning the long-held theory supported by Euclid and Ptolemy that rays emanated from the eyes themselves[11][12]. Critically, he documented his experiments in with enough detail that others could replicate them, establishing a standard of methodological transparency centuries ahead of Europe [11].`)}
        </p>

        <p>
          {parseCitations(`
            Ibn al-Haytham openly criticized earlier thinkers, including Aristotle, for relying too heavily on logic without experimental proof.  Ibn al-Haytham considered systematic induction essential to scientific inquiry, describing theories unsupported by evidence as “superfluous and useless”[7].`)}
        </p>

        <p>
          {parseCitations(`
            Although he is the most well-documented exemplar of this approach, Ibn al-Haytham was part of a broader movement in which Islamic scholars increasingly used controlled tests, quantification, and detailed record-keeping. His work crystallized this tradition into a recognizable method, one that would later influence European scholars through Latin translations.`)}
        </p>
      </section>

      <h2 className="fw-bold mt-5 mb-3">Three Essential Contributions</h2>

      <section>
        <p>
          From Egypt: Systematic observation requires structured documentation. Knowledge that isn’t recorded in detail can’t be transferred or verified.
        </p>
        <p>
          From Greece: Logical reasoning provides frameworks for understanding. Rigorous thinking about cause and effect, about induction and deduction, gives structure to inquiry.
        </p>
        <p>
          From Islamic scholars: Experimental testing combined with detailed documentation enables reproducible science. Hypotheses must be tested systematically, and methods must be shared openly enough for others to verify results.
        </p>

        <p>
          None of these traditions worked in isolation. Greek texts were translated into Arabic during the Islamic Golden Age. Islamic scientific works were later translated into Latin and studied in medieval European universities.
        </p>

        <p>
          What's striking is how early these thinkers recognized principles we're still grappling with today. The reproducibility crisis - the focus of this series - stems from failures in these same fundamentals: incomplete documentation, insufficient detail in methods, reluctance to share negative results, inadequate replication. These aren't new problems. They're recurring challenges that every scientific era must address, with the tools available.
        </p>

        <p>
          In <a href="/resources/history-of-scientific-method-part3">Part 3</a>, we'll see how these ancient foundations were formalized during Europe's Scientific Revolution, when experimenters like Francis Bacon and Robert Boyle transformed individual insights into systematic practice - and when the first scientific societies created institutions dedicated to reproducible research.
        </p>
      </section>

      <blockquote className="blockquote-dark p-4 border-start border-4 border-primary rounded my-5">
        <em>
          The reproducibility crisis — the focus of this series — stems from failures in these same fundamentals: incomplete documentation, insufficient detail in methods, reluctance to share negative results, inadequate replication. These aren't new problems. They're recurring challenges that every scientific era must address.
        </em>
      </blockquote>

      {/* References */}
      <h3 className="fw-semibold mt-4">References</h3>
      <Citations items={citations} />
    </article>
  );
}
